import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip as ChartTooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { MdOutlineThumbUp, MdOutlineThumbDown } from 'react-icons/md';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DatePicker from '../DatePicker';
import ChartCard from '../charts/ChartCard';
import { ChatDashboardTabProps } from '../../types';
import {
  getFeedbackAnalytics,
  FeedbackFilter,
  FeedbackMessageItem,
  FeedbackSummary,
} from '../../utils/feedbackApi';
import {
  buildFeedbackTrendDataset,
  FeedbackTrendDataset,
} from '../../utils/feedbackStats';
import { buildCsvContent, downloadCsv } from '../../utils/exportUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend);

const FEEDBACK_LIMIT = 500;

// ─── CSV export ───────────────────────────────────────────────────────────────

const FEEDBACK_CSV_HEADERS = ['Time', 'User ID', 'Username', 'Feedback', 'Question', 'Answer'];

const buildFeedbackCsvRows = (messages: FeedbackMessageItem[]): string[][] =>
  messages.map((m) => [
    m.question_timestamp,
    String(m.user_id),
    m.username,
    m.user_feedback,
    m.question,
    m.answer,
  ]);

// ─── FeedbackBadge — uses same icons as MessageCard ──────────────────────────

const FeedbackBadge: React.FC<{ type: 'like' | 'dislike' }> = ({ type }) =>
  type === 'like' ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
      <MdOutlineThumbUp className="h-3.5 w-3.5" />
      Like
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
      <MdOutlineThumbDown className="h-3.5 w-3.5" />
      Dislike
    </span>
  );

// ─── Main tab ─────────────────────────────────────────────────────────────────

const FeedbackTab: React.FC<ChatDashboardTabProps> = ({ dateRange, onDateRangeChange }) => {
  const [summary, setSummary]       = useState<FeedbackSummary | null>(null);
  const [trendData, setTrendData]   = useState<FeedbackTrendDataset>({ labels: [], likes: [], dislikes: [] });
  const [messages, setMessages]     = useState<FeedbackMessageItem[]>([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [feedbackFilter, setFeedbackFilter] = useState<FeedbackFilter>('all');

  // ── fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setError('Please select a valid time range.');
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await getFeedbackAnalytics({
          startDate: dateRange.startDate,
          endDate:   dateRange.endDate,
          feedback:  feedbackFilter,
          limit:     FEEDBACK_LIMIT,
          signal:    controller.signal,
        });
        if (!isMounted) return;
        setSummary(resp.summary);
        setTrendData(buildFeedbackTrendDataset(resp.daily_stats));
        setMessages(resp.messages);
      } catch (err) {
        if ((err as Error)?.name === 'AbortError') return;
        if (isMounted) setError('Failed to load feedback data.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; controller.abort(); };
  }, [dateRange.startDate, dateRange.endDate, feedbackFilter]);

  // ── CSV filename ───────────────────────────────────────────────────────────
  const buildCsvFilename = useCallback(
    (prefix: string) => {
      const s = (dateRange.startDate ?? 'start').replace(/:/g, '-');
      const e = (dateRange.endDate   ?? 'end').replace(/:/g, '-');
      return `${prefix}_${s}_to_${e}.csv`;
    },
    [dateRange.startDate, dateRange.endDate]
  );

  // ── export ─────────────────────────────────────────────────────────────────
  const canExport = messages.length > 0;

  const handleExport = useCallback(() => {
    if (!canExport) return;
    const csv = buildCsvContent(FEEDBACK_CSV_HEADERS, buildFeedbackCsvRows(messages));
    const tag  = feedbackFilter === 'all' ? 'all' : feedbackFilter;
    downloadCsv(csv, buildCsvFilename(`feedback_${tag}`));
  }, [messages, feedbackFilter, canExport, buildCsvFilename]);

  const exportAction = useMemo(
    () => (
      <Tooltip title="Export to CSV">
        <span>
          <IconButton size="small" onClick={handleExport} disabled={!canExport}>
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    ),
    [canExport, handleExport]
  );

  // ── chart ──────────────────────────────────────────────────────────────────
  const lineChartData = useMemo(() => ({
    labels: trendData.labels,
    datasets: [
      {
        label: 'Likes',
        data: trendData.likes,
        borderColor: '#22c55e',
        backgroundColor: 'rgba(34,197,94,0.10)',
        borderWidth: 2,
        pointRadius: 3,
        tension: 0.3,
        fill: true,
      },
      {
        label: 'Dislikes',
        data: trendData.dislikes,
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239,68,68,0.08)',
        borderWidth: 2,
        pointRadius: 3,
        tension: 0.3,
        fill: true,
      },
    ],
  }), [trendData]);

  const lineChartOptions: ChartOptions<'line'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' as const },
      tooltip: { mode: 'index', intersect: false },
    },
    scales: {
      y: { beginAtZero: true, ticks: { stepSize: 1, precision: 0 } },
    },
  }), []);

  // ── summary items — same pattern as QuestionAnalysisTab generalRateItems ──
  const summaryItems = [
    {
      label: 'Total Likes',
      value: summary ? summary.total_likes.toLocaleString() : '--',
    },
    {
      label: 'Total Dislikes',
      value: summary ? summary.total_dislikes.toLocaleString() : '--',
    },
    {
      label: 'Like Rate',
      value: summary ? `${(summary.like_rate * 100).toFixed(1)}%` : '--',
    },
  ];

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-3 border-b border-gray-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-3xl font-bold text-gray-900">Feedback</h2>
        <div className="w-full sm:w-auto sm:min-w-[280px]">
          <DatePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={onDateRangeChange}
            className="w-full"
          />
        </div>
      </div>

      {loading && (
        <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
          Loading feedback data...
        </div>
      )}
      {!loading && error && (
        <div className="flex flex-1 items-center justify-center text-sm text-red-500">{error}</div>
      )}

      {!loading && !error && (
        <div className="flex flex-1 min-h-0 flex-col gap-6">

          {/* Summary — same card style as QuestionAnalysisTab */}
          <ChartCard title="Summary">
            <div className="grid h-full gap-4 sm:grid-cols-3">
              {summaryItems.map(({ label, value }) => (
                <div
                  key={label}
                  className="flex flex-col items-center justify-center rounded-2xl border border-gray-100 bg-gray-50 p-4 text-center"
                >
                  <span className="text-3xl font-semibold text-gray-900">{value}</span>
                  <span className="text-sm font-medium text-gray-500">{label}</span>
                </div>
              ))}
            </div>
          </ChartCard>

          {/* Trend chart */}
          <div className="min-h-[240px]">
            <ChartCard title="Feedback Trend">
              {trendData.labels.length > 0 ? (
                <div className="h-full min-h-[190px]">
                  <Line data={lineChartData} options={lineChartOptions} />
                </div>
              ) : (
                <div className="flex h-full items-center justify-center text-sm text-gray-400">
                  No trend data for the selected range.
                </div>
              )}
            </ChartCard>
          </div>

          {/* Message list */}
          <div className="flex min-h-0 flex-1 flex-col" style={{ minHeight: '380px' }}>
            <ChartCard
              title="Feedback Messages"
              scrollable
              fullHeight
              actions={exportAction}
            >
              {/* Filter buttons — same style as TabSider active state */}
              <div className="mb-3 flex items-center gap-2">
                {(['all', 'like', 'dislike'] as FeedbackFilter[]).map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFeedbackFilter(f)}
                    className={`flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                      feedbackFilter === f
                        ? 'bg-blue-50 text-blue-700 border border-blue-100'
                        : 'text-gray-600 hover:bg-gray-50 border border-transparent'
                    }`}
                  >
                    {f === 'like' && <MdOutlineThumbUp className="h-3.5 w-3.5" />}
                    {f === 'dislike' && <MdOutlineThumbDown className="h-3.5 w-3.5" />}
                    {f === 'all' ? 'All' : f === 'like' ? 'Like' : 'Dislike'}
                  </button>
                ))}
                <span className="ml-auto text-xs text-gray-400">
                  {messages.length} message{messages.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Table header — same pattern as QuestionAnalysisTab list headers */}
              <div className="flex items-center px-1 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <span className="w-24 flex-shrink-0">Time</span>
                <span className="w-28 flex-shrink-0">User</span>
                <span className="w-20 flex-shrink-0">Feedback</span>
                <span className="flex-1">Question</span>
                <span className="ml-4 w-64 flex-shrink-0">Answer</span>
              </div>

              {/* Rows */}
              <div
                className="custom-scrollbar flex-1 min-h-0 overflow-y-auto pr-2"
                style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 #f1f5f9' }}
              >
                {messages.length === 0 ? (
                  <div className="flex h-32 items-center justify-center text-sm text-gray-400">
                    No feedback messages for the selected filters.
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className="flex items-start gap-2 border-t border-gray-100 px-1 py-3 text-sm text-gray-700"
                    >
                      <span className="w-24 flex-shrink-0 text-xs text-gray-500">
                        {msg.question_timestamp.slice(0, 10)}
                      </span>
                      <Tooltip title={`User ID: ${msg.user_id}`} arrow>
                        <span className="w-28 flex-shrink-0 cursor-default truncate font-medium text-gray-900">
                          {msg.username}
                        </span>
                      </Tooltip>
                      <span className="w-20 flex-shrink-0">
                        <FeedbackBadge type={msg.user_feedback} />
                      </span>
                      <Tooltip title={msg.question} arrow>
                        <span className="flex-1 cursor-default truncate text-gray-600">
                          {msg.question}
                        </span>
                      </Tooltip>
                      <Tooltip title={msg.answer} arrow>
                        <span className="ml-4 w-64 flex-shrink-0 cursor-default truncate text-gray-500">
                          {msg.answer}
                        </span>
                      </Tooltip>
                    </div>
                  ))
                )}
              </div>
            </ChartCard>
          </div>

        </div>
      )}
    </div>
  );
};

export default FeedbackTab;